import { StaticImage } from "gatsby-plugin-image"
import { ReactNode } from "react"
import { AiOutlineYoutube } from "react-icons/ai"
import { FaFacebookF, FaInstagram, FaLinkedinIn } from "react-icons/fa"
import IND from "../images/icons/new/IND.svg"
import UAE from "../images/icons/new/UAE.svg"
import Premagic_Logo from "../images/logo/premagic-logo-white.svg"

import { blogUrl } from "../services/blogLinks"
import useWindowDimensions from "../utils/useWindowDimensions"
import { AISummaries } from "./AISummaries"
import { Link } from "gatsby"
import { footer } from "./footer.module.css"

function FooterColumn({
  title,
  children,
}: {
  title: string
  children: ReactNode
}) {
  return (
    <div>
      <h4 className="text-xs font-semibold tracking-widest uppercase text-white/50 mb-5">
        {title}
      </h4>
      <ul className="space-y-2.5 text-sm">{children}</ul>
    </div>
  )
}

function FooterLink({
  to,
  external = false,
  children,
}: {
  to: string
  external?: boolean
  children: ReactNode
}) {
  const className = "text-white/70 hover:text-white transition"
  if (external) {
    return (
      <li>
        <a
          href={to}
          target="_blank"
          rel="noopener noreferrer"
          className={className}
        >
          {children}
        </a>
      </li>
    )
  }
  return (
    <li>
      <Link to={to} className={className}>
        {children}
      </Link>
    </li>
  )
}

export default function Footer() {
  const year = new Date().getFullYear()
  const { width } = useWindowDimensions()
  const isMobileUp = width >= 768

  const isBrowser = () => typeof window !== "undefined"

  const isUAEDomain = isBrowser() && window.location.host.includes("ae")
  const showUAEAddress = isUAEDomain || false

  return (
    <footer className="bg-[#5B34D6] text-white relative overflow-hidden">
      <div className="relative z-10">
        {/* bottom glow */}
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 z-[-1] md:w-[500px] w-[700px] ">
          <StaticImage
            src="../images/vectors/bottom-shade.png"
            alt="bottom-shade"
            className="w-full h-full object-cover "
          />
        </div>

        <div className="max-w-7xl mx-auto pt-12 md:pt-24 pb-12 relative z-10 px-6 md:px-0">
          <div className="flex md:items-center flex-col md:flex-row gap-6 md:gap-20 pb-16 border-b border-white/20">
            <div className="w-full md:w-1/2 mr-10">
              <div className="mb-8">
                <img src={Premagic_Logo} width={150} alt="Premagic" />
              </div>

              <h2 className="text-[18px] md:text-[20px] leading-[1.35] font-semibold md:max-w-[500px] w-full">
                Premagic is an AI-powered event tech platform designed to boost
                organic event marketing by turning attendees into marketers.
              </h2>
              {isMobileUp && (
                <div className="mt-10">
                  <AISummaries />
                </div>
              )}
            </div>

            <div className="flex items-center md:w-[60%]">
              <p className="text-white leading-relaxed text-sm md:text-lg max-w-2xl">
                By delivering personalised content to attendees from pre-event
                to post-event, Premagic elevates the attendee experience and
                encourages them to create and share authentic event content that
                speaks louder than traditional advertising.
              </p>
            </div>
            {!isMobileUp && <AISummaries />}
          </div>

          <div className="py-14">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8 gap-y-12">
              <FooterColumn title="Products">
                <FooterLink to="/create-event-marketing-posters/">
                  Grow Registrations
                </FooterLink>
                <FooterLink to="/smart-photo-distribution-for-seamless-events/">
                  Amplify Reach
                </FooterLink>
                <FooterLink to="/avatar-studio/">Spark Buzz</FooterLink>
                <FooterLink to="/integrations/">Integrations</FooterLink>
              </FooterColumn>

              <FooterColumn title="Learn">
                <FooterLink to={blogUrl("home")} external>
                  Blog
                </FooterLink>
                <FooterLink to="/case-study/">Case Studies</FooterLink>
                <FooterLink to="/reports/">Reports</FooterLink>
                <FooterLink to="/integrations/how-it-works/">
                  Integration Guide
                </FooterLink>
                <FooterLink to="/faq">FAQ</FooterLink>
              </FooterColumn>

              <FooterColumn title="Evaluate">
                <FooterLink to="/compare/ingo-vs-premagic/">
                  Premagic vs Ingo
                </FooterLink>
                <FooterLink to="/request-a-demo/">Book a Demo</FooterLink>
              </FooterColumn>

              <FooterColumn title="Support">
                <FooterLink
                  to="https://support.premagic.com/hc/home"
                  external
                >
                  Help Center
                </FooterLink>
                <FooterLink to="https://status.premagic.com/" external>
                  Status
                </FooterLink>
                <FooterLink to="/partners/">Partners</FooterLink>
              </FooterColumn>

              <FooterColumn title="Company">
                <FooterLink to="/about-us">About</FooterLink>
                <FooterLink to="/contact-us">Contact</FooterLink>
                <FooterLink
                  to="https://wellfound.com/company/premagic/jobs"
                  external
                >
                  Careers
                </FooterLink>
                <FooterLink to="https://app.premagic.com/" external>
                  Login
                </FooterLink>
              </FooterColumn>
            </div>

            <div className="mt-16 pt-10 border-t border-white/20 grid grid-cols-1 md:grid-cols-2 gap-10">
              {/* DUBAI */}
              <div>
                <div className="flex items-center gap-2 font-semibold text-sm mb-4">
                  <img src={UAE} alt="UAE" />
                  DUBAI
                </div>

                <p className="text-white/80 text-sm leading-6">
                  Premagic CO LLC <br />
                  Office B2007-235, Latifa Tower <br />
                  Sheikh Zayed Road, Dubai <br />
                  United Arab Emirates
                </p>

                <div className="mt-4 text-sm space-y-1.5">
                  <a
                    href="mailto:sales@premagic.com"
                    className="block text-white/70 hover:text-white transition"
                  >
                    sales@premagic.com
                  </a>
                  <a
                    href="tel:+971585337576"
                    className="block text-white/70 hover:text-white transition"
                  >
                    +971 58 533 7576
                  </a>
                  <a
                    href="tel:+971529453727"
                    className="block text-white/70 hover:text-white transition"
                  >
                    +971 52 945 3727
                  </a>
                </div>
              </div>

              {/* INDIA */}
              <div>
                <div className="flex items-center gap-2 font-semibold text-sm mb-4">
                  <img src={IND} alt="INDIA" />
                  INDIA
                </div>

                <p className="text-white/80 text-sm leading-6">
                  Wellbeing Studios Private LTD <br />
                  IX/483, Grand Central By Group, <br />
                  Opp. Infopark Road, Edachira P O, <br />
                  Kakkanad, Kochi, Kerala
                </p>

                <div className="mt-4 text-sm space-y-1.5">
                  <a
                    href="mailto:support@premagic.com"
                    className="block text-white/70 hover:text-white transition"
                  >
                    support@premagic.com
                  </a>
                  <a
                    href="tel:+917829419174"
                    className="block text-white/70 hover:text-white transition"
                  >
                    +91 7829 419 174
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-white/20 pt-8 flex flex-col md:flex-row items-center justify-between gap-6 mt-10 md:mt-0">
            <p className="text-white/60 text-sm text-center">
              © {year} Wellbeing Studios Pvt. Ltd. Premagic {isMobileUp && "|"}
              <Link
                to={blogUrl("privacy")}
                target="_blank"
                className="inline mx-2 text-white/70 hover:text-white"
              >
                Privacy
              </Link>
              |
              <Link
                to={blogUrl("terms")}
                target="_blank"
                className="inline mx-2 text-white/70 hover:text-white"
              >
                Terms & Conditions
              </Link>
            </p>

            <div className="flex gap-6 text-lg">
              <Link
                to="https://www.linkedin.com/company/premagic/"
                target="_blank"
                className="text-white transition-transform duration-300 hover:scale-110"
              >
                <FaLinkedinIn />
              </Link>

              <Link
                to="https://www.instagram.com/premagicdotcom/"
                target="_blank"
                className="text-white transition-transform duration-300 hover:scale-110"
              >
                <FaInstagram />
              </Link>

              <Link
                to="https://www.facebook.com/premagicdotcom/"
                target="_blank"
                className="text-white transition-transform duration-300 hover:scale-110"
              >
                <FaFacebookF size={16} />
              </Link>

              <Link
                to="https://www.youtube.com/channel/UCyhi-GvR0rrBq639Z8uWtqw/videos"
                target="_blank"
                className="text-white transition-transform duration-300 hover:scale-110"
              >
                <AiOutlineYoutube size={20} />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}
